import * as XLSX from 'xlsx';

export function parseCSVText(csvText: string): string {
  if (!csvText || !csvText.trim()) {
    throw new Error('CSV text is empty');
  }

  const lines = csvText.split(/\r?\n/).filter((line) => line.trim());
  if (lines.length === 0) {
    throw new Error('No data found in CSV');
  }

  const firstLine = lines[0];
  const hasTabs = firstLine.includes('\t');
  const delimiter = hasTabs ? '\t' : ',';

  const rows = lines.map((line) => {
    const values: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      const nextChar = line[i + 1];

      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === delimiter && !inQuotes) {
        values.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    values.push(current.trim());
    return values;
  });

  if (rows.length === 0) {
    throw new Error('No rows found in CSV');
  }

  const headers = rows[0];
  const dataRows = rows.slice(1);

  let formatted = `Column headers: ${headers.join(', ')}\n`;
  dataRows.forEach((row, index) => {
    formatted += `Row ${index + 1}: ${row.join(', ')}\n`;
  });

  return formatted;
}

export async function parseExcelFile(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });

        const firstSheetName = workbook.SheetNames[0];
        if (!firstSheetName) {
          reject(new Error('Excel file has no sheets'));
          return;
        }

        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          defval: '',
        }) as string[][];

        if (jsonData.length === 0) {
          reject(new Error('Excel sheet is empty'));
          return;
        }

        const headers = jsonData[0] as string[];
        const dataRows = jsonData.slice(1) as string[][];

        let formatted = `Column headers: ${headers.join(', ')}\n`;
        dataRows.forEach((row, index) => {
          formatted += `Row ${index + 1}: ${row.join(', ')}\n`;
        });

        resolve(formatted);
      } catch (error) {
        reject(
          new Error(
            `Failed to parse Excel file: ${error instanceof Error ? error.message : String(error)}`,
          ),
        );
      }
    };

    reader.onerror = () => reject(new Error('Failed to read Excel file'));
    reader.readAsArrayBuffer(file);
  });
}

export async function parseCSVFile(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        resolve(parseCSVText(text));
      } catch (error) {
        reject(
          new Error(
            `Failed to parse CSV file: ${error instanceof Error ? error.message : String(error)}`,
          ),
        );
      }
    };

    reader.onerror = () => reject(new Error('Failed to read CSV file'));
    reader.readAsText(file);
  });
}
